import type {
  FormElement,
  FormData,
  ConditionalLogic,
  ConditionalLogicRule,
  DisplayLogic,
  VariableMapping,
} from '../types';
import { booleanToNumeric, normalizeBooleanValue } from '../utils/valueNormalization';

/**
 * Conditional Logic Evaluator for MediForm SDK
 *
 * Evaluates show/hide conditions, calculations, and interpretations
 * based on form data.
 */

export interface EvaluationContext {
  formData: FormData;
  variables: Record<string, any>;
  calculatedValues: Record<string, any>;
}

export interface ConditionalState {
  visible: boolean;
  required: boolean;
  highlighted: boolean;
}

export class ConditionalLogicEvaluator {
  private formDefinition: FormElement;
  private formData: FormData;
  private variables: Record<string, any>;
  private calculatedValues: Record<string, any>;
  private allElements: FormElement[];
  private allCalculatedLabels: FormElement[];

  constructor(formDefinition: FormElement) {
    this.formDefinition = formDefinition;
    this.formData = {};
    this.variables = {};
    this.calculatedValues = {};
    this.allElements = this.getAllElements(formDefinition);
    this.allCalculatedLabels = this.getAllCalculatedLabels(formDefinition);
  }

  /**
   * Update form data and recalculate all conditions
   */
  updateData(data: FormData): void {
    this.formData = data;
    this.updateVariables();
    this.updateCalculatedValues();
  }

  /**
   * Get all calculated values
   */
  getCalculatedValues(): Record<string, any> {
    return { ...this.calculatedValues };
  }

  /**
   * Get conditional state for a specific element
   */
  getElementState(elementId: string): ConditionalState {
    const element = this.findElementById(this.formDefinition, elementId);
    if (!element || !element.properties?.conditionalLogic) {
      return { visible: true, required: element?.properties?.required || false, highlighted: false };
    }

    const logic = element.properties.conditionalLogic;
    const state: ConditionalState = {
      visible: true,
      required: element.properties.required || false,
      highlighted: false,
    };

    // Update variables from variable mapping
    if (logic.variableMapping) {
      this.updateVariablesFromMapping(logic.variableMapping);
    }

    // Evaluate each rule
    for (const rule of logic.rules) {
      const conditionMet = this.evaluateCondition(rule.condition);

      switch (rule.action) {
        case 'show':
          if (!conditionMet) state.visible = false;
          break;
        case 'hide':
          if (conditionMet) state.visible = false;
          break;
        case 'require':
          if (conditionMet) state.required = true;
          break;
        case 'highlight':
          if (conditionMet) state.highlighted = true;
          break;
      }
    }

    return state;
  }

  /**
   * Evaluate display logic (calculations and interpretations)
   */
  evaluateDisplayLogic(displayLogic: DisplayLogic): any {
    // Update variables from mapping
    if (displayLogic.variableMapping) {
      this.updateVariablesFromMapping(displayLogic.variableMapping);
    }

    if (displayLogic.type === 'calculation' && displayLogic.calculationFormula) {
      return this.evaluateCalculation(displayLogic.calculationFormula);
    }

    if (displayLogic.type === 'interpretation' && displayLogic.interpretationRules) {
      return this.evaluateInterpretation(displayLogic.interpretationRules);
    }

    return null;
  }

  /**
   * Update variables from the entire form
   */
  private updateVariables(): void {
    this.variables = {};
    this.collectVariables(this.formDefinition);
  }

  /**
   * Update variables from variable mapping (matches Test View implementation)
   */
  private updateVariablesFromMapping(mapping: VariableMapping[]): void {
    mapping.forEach(({ variable, fieldId, useScore }) => {
      // Check calculated values first, then form data
      let value = fieldId in this.calculatedValues ? this.calculatedValues[fieldId] : this.formData[fieldId];
      const element = this.findElementById(this.formDefinition, fieldId);

      // Initialize checkbox as empty array if undefined
      if (element?.type === 'checkbox' && value === undefined) {
        value = [];
      }

      if (useScore === true) {
        // Get score from selected option
        this.variables[variable] = this.getScoreForAnswer(fieldId, value);
      } else if (useScore === false) {
        // Explicitly NOT using score - use the raw value
        this.variables[variable] = value;
      } else if (element?.type === 'yes-no' || element?.type === 'single-checkbox') {
        // Handle yes-no and single-checkbox fields (boolean values)
        // Default behavior: convert to 1/0
        this.variables[variable] = booleanToNumeric(value);
      } else if (element?.type === 'radio' || element?.type === 'checkbox') {
        // For radio/checkbox questions without explicit useScore setting,
        // default to using scores (for backward compatibility)
        this.variables[variable] = this.getScoreForAnswer(fieldId, value);
      } else {
        // Use the actual value (not the score)
        this.variables[variable] = value;
      }
    });
  }

  /**
   * Recursively collect variables from form elements
   */
  private collectVariables(element: FormElement): void {
    // Map field IDs to their values as variables
    if (this.isField(element.type)) {
      const varName = element.properties?.name || element.id;
      const rawValue = this.formData[element.id];
      if (element.type === 'yes-no' || element.type === 'single-checkbox') {
        this.variables[varName] = normalizeBooleanValue(rawValue);
      } else {
        this.variables[varName] = rawValue;
      }
    }

    // Process questionnaire grid rows
    if (element.type === 'questionnaire-grid' && element.properties?.gridRows) {
      element.properties.gridRows.forEach(row => {
        const varName = row.id;
        this.variables[varName] = this.formData[row.id];
      });
    }

    // Recurse through children
    if (element.children) {
      element.children.forEach(child => this.collectVariables(child));
    }
  }

  /**
   * Evaluate a condition expression
   */
  private evaluateCondition(condition: string): boolean {
    try {
      // Replace variable names with their values
      let expression = condition;

      // Replace variables in the expression
      Object.keys(this.variables).forEach(varName => {
        const value = this.variables[varName];
        const regex = new RegExp(`\\b${varName}\\b`, 'g');

        // Handle different value types
        if (typeof value === 'string') {
          expression = expression.replace(regex, `"${value}"`);
        } else if (value === null || value === undefined) {
          expression = expression.replace(regex, 'null');
        } else {
          expression = expression.replace(regex, String(value));
        }
      });

      // Evaluate the expression safely
      // In production, use a safe expression evaluator like expr-eval or jsep
      // For now, we'll use a simplified approach
      return this.safeEval(expression);
    } catch (error) {
      console.error('Error evaluating condition:', condition, error);
      return false;
    }
  }

  /**
   * Evaluate a calculation formula (matches Test View implementation)
   */
  private evaluateCalculation(formula: string): number | null {
    try {
      const customFunctions = {
        SUM: (...args: (number | undefined)[]) => args.reduce((acc: number, val) => acc + (val || 0), 0),
        AVG: (...args: (number | undefined)[]) => {
          const filteredArgs = args.filter(v => v !== undefined) as number[];
          const sum = customFunctions.SUM(...filteredArgs);
          return filteredArgs.length ? sum / filteredArgs.length : 0;
        },
        MIN: (...args: (number | undefined)[]) => {
          const filteredArgs = args.filter(v => v !== undefined) as number[];
          return filteredArgs.length ? Math.min(...filteredArgs) : 0;
        },
        MAX: (...args: (number | undefined)[]) => {
          const filteredArgs = args.filter(v => v !== undefined) as number[];
          return filteredArgs.length ? Math.max(...filteredArgs) : 0;
        },
        COUNT: (...args: (number | undefined)[]) => {
          return args.filter(v => v !== undefined).length;
        }
      };

      // Create variable declarations for all variables in the variables object
      const variableDeclarations = Object.keys(this.variables)
        .map(varName => {
          const value = this.variables[varName];
          let valueStr;
          if (typeof value === 'string') {
            // Quote string values and escape any quotes within
            valueStr = `'${value.replace(/'/g, "\\'")}'`;
          } else if (value === null || value === undefined) {
            valueStr = '0';
          } else {
            valueStr = String(value);
          }
          return `let ${varName} = ${valueStr};`;
        })
        .join('\n');

      const functionBody = `
        "use strict";
        ${variableDeclarations}
        return ${formula};
      `;

      const func = new Function('Math', 'SUM', 'AVG', 'MIN', 'MAX', 'COUNT', functionBody);
      const result = func(Math, customFunctions.SUM, customFunctions.AVG, customFunctions.MIN, customFunctions.MAX, customFunctions.COUNT);

      return typeof result === 'number' && !isNaN(result) ? result : null;
    } catch (error) {
      console.error('Error evaluating calculation:', formula, error);
      return null;
    }
  }

  /**
   * Evaluate interpretation rules (matches Test View implementation)
   */
  private evaluateInterpretation(rules: string | any[]): string {
    let rulesString = '';
    if (Array.isArray(rules)) {
      rulesString = rules.map((rule, index) => {
        const keyword = index === 0 ? 'if' : 'else if';
        return `${keyword} (${rule.condition}) { return "${rule.output.replace(/"/g, '\\"')}"; }`;
      }).join(' ');
      rulesString += ' else { return "No interpretation met"; }';
    } else {
      rulesString = rules;
    }

    try {
      const variableDeclarations = Object.keys(this.variables)
        .map(varName => {
          const value = this.variables[varName];
          let val;
          if (typeof value === 'string') {
            val = `'${value.replace(/'/g, "\\'")}'`;
          } else if (value === undefined || value === null) {
            val = String(value);
          } else {
            val = JSON.stringify(value);
          }
          return `let ${varName} = ${val};`;
        })
        .join('\n');

      const functionBody = `
        "use strict";
        ${variableDeclarations}
        ${rulesString}
      `;

      const func = new Function(functionBody);
      const result = func();

      return String(result);
    } catch (error) {
      console.error('Error evaluating interpretation:', rules, error);
      return 'Error';
    }
  }

  /**
   * Safe evaluation of boolean expressions
   * NOTE: This is a simplified implementation. In production, use a library
   * like expr-eval, jsep, or json-logic-js for security
   */
  private safeEval(expression: string): boolean {
    // Remove whitespace
    expression = expression.trim();

    // Handle simple comparisons
    const comparisons = [
      { op: '===', fn: (a: any, b: any) => a === b },
      { op: '!==', fn: (a: any, b: any) => a !== b },
      { op: '==', fn: (a: any, b: any) => a == b },
      { op: '!=', fn: (a: any, b: any) => a != b },
      { op: '>=', fn: (a: any, b: any) => Number(a) >= Number(b) },
      { op: '<=', fn: (a: any, b: any) => Number(a) <= Number(b) },
      { op: '>', fn: (a: any, b: any) => Number(a) > Number(b) },
      { op: '<', fn: (a: any, b: any) => Number(a) < Number(b) },
    ];

    for (const { op, fn } of comparisons) {
      if (expression.includes(op)) {
        const [left, right] = expression.split(op).map(s => s.trim());
        const leftVal = this.parseValue(left);
        const rightVal = this.parseValue(right);
        return fn(leftVal, rightVal);
      }
    }

    // Handle logical operators
    if (expression.includes('&&')) {
      const parts = expression.split('&&').map(s => s.trim());
      return parts.every(part => this.safeEval(part));
    }

    if (expression.includes('||')) {
      const parts = expression.split('||').map(s => s.trim());
      return parts.some(part => this.safeEval(part));
    }

    if (expression.startsWith('!')) {
      return !this.safeEval(expression.substring(1).trim());
    }

    // Handle boolean literals
    if (expression === 'true') return true;
    if (expression === 'false') return false;

    // Handle null/undefined
    if (expression === 'null' || expression === 'undefined') return false;

    // Default: truthy check
    const value = this.parseValue(expression);
    return Boolean(value);
  }

  /**
   * Safe evaluation of mathematical expressions
   */
  private safeEvalMath(expression: string): number {
    // Remove whitespace
    expression = expression.trim();

    try {
      // Very basic math evaluation - replace with proper library in production
      // This handles simple arithmetic: +, -, *, /, ()

      // Replace common math functions
      expression = expression.replace(/\babs\(([^)]+)\)/g, (_, num) => {
        return String(Math.abs(this.safeEvalMath(num)));
      });

      expression = expression.replace(/\bround\(([^)]+)\)/g, (_, num) => {
        return String(Math.round(this.safeEvalMath(num)));
      });

      expression = expression.replace(/\bfloor\(([^)]+)\)/g, (_, num) => {
        return String(Math.floor(this.safeEvalMath(num)));
      });

      expression = expression.replace(/\bceil\(([^)]+)\)/g, (_, num) => {
        return String(Math.ceil(this.safeEvalMath(num)));
      });

      // For production, use a library like mathjs or expr-eval
      // WARNING: Never use eval() in production!
      // This is a placeholder that handles basic arithmetic
      const result = Function(`'use strict'; return (${expression})`)();
      return typeof result === 'number' && !isNaN(result) ? result : 0;
    } catch (error) {
      console.error('Error in math evaluation:', expression, error);
      return 0;
    }
  }

  /**
   * Parse a value from string representation
   */
  private parseValue(str: string): any {
    str = str.trim();

    // String literal
    if ((str.startsWith('"') && str.endsWith('"')) ||
        (str.startsWith("'") && str.endsWith("'"))) {
      return str.slice(1, -1);
    }

    // Number
    if (!isNaN(Number(str))) {
      return Number(str);
    }

    // Boolean
    if (str === 'true') return true;
    if (str === 'false') return false;

    // Null/undefined
    if (str === 'null' || str === 'undefined') return null;

    // Variable value
    return str;
  }

  /**
   * Find element by ID in the form tree
   */
  private findElementById(element: FormElement, id: string): FormElement | null {
    if (element.id === id) return element;

    if (element.children) {
      for (const child of element.children) {
        const found = this.findElementById(child, id);
        if (found) return found;
      }
    }

    return null;
  }

  /**
   * Check if element type is a field
   */
  private isField(type: string): boolean {
    const fieldTypes = [
      'text',
      'number',
      'textarea',
      'radio',
      'checkbox',
      'single-checkbox',
      'date',
      'yes-no',
    ];
    return fieldTypes.includes(type);
  }

  /**
   * Get all elements from form definition (including grid rows)
   */
  private getAllElements(node: FormElement, elements: FormElement[] = []): FormElement[] {
    elements.push(node);
    if (node.children) {
      node.children.forEach(child => this.getAllElements(child, elements));
    }
    if (node.type === 'questionnaire-grid' && node.properties?.gridRows) {
      const gridAsQuestionElements = node.properties.gridRows.map(row => ({
        id: row.id,
        type: node.properties!.controlType || 'radio',
        properties: {
          name: row.question,
          options: node.properties!.gridColumns?.map(col => ({
            label: col.header,
            value: col.value,
            score: col.score,
          }))
        }
      })) as FormElement[];
      elements.push(...gridAsQuestionElements);
    }
    return elements;
  }

  /**
   * Get all calculated/interpretation label elements
   */
  private getAllCalculatedLabels(formDefinition: FormElement): FormElement[] {
    const calculatedLabels: FormElement[] = [];
    const traverse = (node: FormElement) => {
      if (node.type === 'label' && (node.properties?.isCalculated || node.properties?.isInterpretation) && node.properties?.displayLogic) {
        calculatedLabels.push(node);
      }
      node.children?.forEach(traverse);
    };
    traverse(formDefinition);
    return calculatedLabels;
  }

  /**
   * Get score for an answer (matches Test View implementation)
   */
  private getScoreForAnswer(questionId: string, answerValue: any): number {
    const question = this.allElements.find(el => el.id === questionId);
    if (!question || answerValue === undefined) {
      if (typeof answerValue === 'string') {
        const parsed = parseFloat(answerValue);
        return isNaN(parsed) ? 0 : parsed;
      }
      return 0;
    }

    // Handle yes-no and single-checkbox fields (boolean values)
    if (question.type === 'yes-no' || question.type === 'single-checkbox') {
      return booleanToNumeric(answerValue);
    }

    const options = question.properties?.options;
    if (!options) {
      const parsed = parseFloat(answerValue);
      return isNaN(parsed) ? 0 : parsed;
    }

    if (Array.isArray(answerValue)) { // Checkbox
      return answerValue.reduce((checkboxScore, val) => {
        const option = options.find(o => o.value === val);
        return checkboxScore + (option?.score || 0);
      }, 0);
    } else { // Radio or Select
      const option = options.find(o => o.value == answerValue); // Use == for loose comparison of number and string
      return option?.score || 0;
    }
  }

  /**
   * Update calculated values from form data (matches Test View implementation)
   */
  private updateCalculatedValues(): void {
    const results: Record<string, any> = {};

    // Sort labels to process dependencies in order (topological sort)
    const sortedLabels = [...this.allCalculatedLabels].sort((a, b) => {
      const aDeps = a.properties?.displayLogic?.dependencies || [];
      const bDeps = b.properties?.displayLogic?.dependencies || [];
      const aOutput = a.properties?.displayLogic?.outputVariable;
      const bOutput = b.properties?.displayLogic?.outputVariable;

      if (aOutput && bDeps.includes(aOutput)) return -1;
      if (bOutput && aDeps.includes(bOutput)) return 1;
      return 0;
    });

    sortedLabels.forEach(label => {
      const logic = label.properties?.displayLogic;
      if (!logic || !logic.outputVariable) return;

      let mapping: VariableMapping[] = [];
      if (Array.isArray(logic.variableMapping)) {
        mapping = logic.variableMapping;
      } else if (typeof logic.variableMapping === 'object') {
        mapping = Object.entries(logic.variableMapping).map(([variable, fieldId]) => ({
          variable,
          fieldId: fieldId as string
        }));
      }

      const allDependenciesMet = (logic.dependencies || []).every(depId =>
        (this.formData[depId] !== undefined && this.formData[depId] !== '') ||
        (results[depId] !== undefined && results[depId] !== '')
      );

      if (!allDependenciesMet) {
        results[logic.outputVariable] = '';
        return;
      }

      const variableValues: Record<string, any> = {};
      mapping.forEach(vm => {
        const fieldId = vm.fieldId || (vm as any).questionId;
        if (!fieldId) return;

        // A dependency can be another calculated value, so check results first.
        let answerValue = fieldId in results ? results[fieldId] : this.formData[fieldId];
        const question = this.allElements.find(el => el.id === fieldId);

        // If useScore is explicitly set, respect it
        if (vm.useScore === true) {
          variableValues[vm.variable] = this.getScoreForAnswer(fieldId, answerValue);
        } else if (vm.useScore === false) {
          // Explicitly NOT using score - use the raw value
          variableValues[vm.variable] = answerValue;
        } else if (question?.type === 'yes-no' || question?.type === 'single-checkbox') {
          // Handle yes-no and single-checkbox fields (boolean values)
          // Default behavior: convert to 1/0
          variableValues[vm.variable] = booleanToNumeric(answerValue);
        } else if (question?.type === 'radio' || question?.type === 'checkbox') {
          // For radio/checkbox questions without explicit useScore setting,
          // default to using scores (for backward compatibility)
          variableValues[vm.variable] = this.getScoreForAnswer(fieldId, answerValue);
        } else {
          // Use the actual value (not the score)
          const parsed = parseFloat(answerValue);
          variableValues[vm.variable] = isNaN(parsed) ? answerValue : parsed;
        }
      });

      // Update variables for calculation/interpretation
      this.variables = { ...this.variables, ...variableValues };

      if (logic.type === 'calculation' && logic.calculationFormula) {
        results[logic.outputVariable] = this.evaluateCalculation(logic.calculationFormula);
      } else if (logic.type === 'interpretation' && logic.interpretationRules) {
        results[logic.outputVariable] = this.evaluateInterpretation(logic.interpretationRules);
      }
    });

    this.calculatedValues = results;
  }
}

/**
 * Helper function to evaluate conditional logic for an element
 */
export function evaluateConditionalLogic(
  formDefinition: FormElement,
  elementId: string,
  formData: FormData
): ConditionalState {
  const evaluator = new ConditionalLogicEvaluator(formDefinition);
  evaluator.updateData(formData);
  return evaluator.getElementState(elementId);
}

/**
 * Helper function to evaluate display logic
 */
export function evaluateDisplayLogicValue(
  formDefinition: FormElement,
  displayLogic: DisplayLogic,
  formData: FormData
): any {
  const evaluator = new ConditionalLogicEvaluator(formDefinition);
  evaluator.updateData(formData);
  return evaluator.evaluateDisplayLogic(displayLogic);
}
